import { Profile, ProfileTier } from '../interfaces';
import {
  AirCalendarPrice,
  AmHolidayTypes,
  AmLocation,
  AmLocations,
  AmRooms,
  Availability,
  Calendar,
  LatLong,
  Location,
  Locations,
  LocationSuggestions,
  LocationType,
  PaxAmount,
  Price,
  Prices,
  TravelClass,
  TravelDates,
  TravelType,
  TrendingDestinations,
  WeeklyFlights,
  WidgetTab,
  ContentType,
  HistogramData,
} from '../common';
import { EnvConfig, ExtensionApi, WidgetTestVariant } from '../config';
import { Localizations, localizations } from '../i18n';
import { Service } from '../services';

// == Public constants =========================================================

export const DEFAULT_TRAVEL_CLASSES: TravelClass[] = [TravelClass.ECONOMY, TravelClass.BUSINESS];

// == User selections (persistent) =============================================

export interface SelectionState {
  locations: Locations[];
  activeLocationIndex: number;
  paxAmount: PaxAmount;
  travelClass: TravelClass;
  travelDates: TravelDates;
  travelType: TravelType;
  locale?: string;
  directFlights: boolean;
  activatedTab?: WidgetTab;
  promoCode: string | null;
  promoCodeTerms: string | null;
  promoCodeTermsTitle: string | null;
  promoCodeNotFoundError: boolean;
  isCountryShopTestGroup?: boolean;
}

export const initialSelectionState: SelectionState = {
  travelType: TravelType.ROUND_TRIP,
  paxAmount: {
    adults: 1,
    children: 0,
    c15s: 0,
    infants: 0,
  },
  activeLocationIndex: 0,
  travelClass: TravelClass.ECONOMY,
  locations: [{}],
  travelDates: {},
  directFlights: false,
  activatedTab: WidgetTab.FLIGHT,
  promoCode: null,
  promoCodeTerms: null,
  promoCodeTermsTitle: null,
  promoCodeNotFoundError: false,
  isCountryShopTestGroup: false,
};

export interface AmDurationAvailability {
  [durationKey: string]: number;
}

export interface AmAvailability {
  [dateKey: string]: AmDurationAvailability;
}

export interface AmSelectionState {
  travelType: TravelType;
  locations: AmLocations;
  rooms: AmRooms;
  duration?: string;
  travelClass: TravelClass;
  amTravelDates: TravelDates;
  directFlights: boolean;
  amProceedEnabled: boolean;
}
export interface AmState {
  amSelection: AmSelectionState;
  origins: Location[];
  destinations: AmLocation[];
  availability: AmAvailability;
  holidayTypes: AmHolidayTypes;
}

export const initialAmSelectionState: AmSelectionState = {
  travelType: TravelType.ONE_WAY,
  locations: {},
  rooms: [
    {
      adults: 2,
      children: 0,
      childrenAges: [],
      roomIndex: 0,
    },
  ],
  travelClass: TravelClass.MIXED,
  amTravelDates: {},
  directFlights: false,
  amProceedEnabled: false,
};

export const initialAmState: AmState = {
  amSelection: initialAmSelectionState,
  origins: [],
  destinations: [],
  holidayTypes: {},
  availability: {},
};

// == UX State (non-persistent) ================================================

export enum Warning {
  NO_FLIGHTS = 'noFlights',
  SEASONAL_ROUTE = 'seasonalRoute',
  REGULATORY = 'regulatory',
  ROUTE_NOT_SUPPORTED = 'routeNotSupported',
}

export type Warnings = { [key in Warning]?: boolean };

export type Loading = { [key in Service]?: boolean };

export interface I18nState {
  locale?: string;
  localizations?: Localizations;
}

export enum CompactWidgetState {
  CLOSED = 'CLOSED',
  OPENED = 'OPENED',
}

export interface UxState {
  // TODO: Errors
  warnings: Warnings;
  loading: Loading;
  i18n: I18nState;
  calendar?: Calendar;
  currentDate?: string;
  userLocation?: LatLong;
  proceedEnabled?: boolean;
  departureDateEnabled?: boolean;
  returnDateEnabled?: boolean;
  proceedToBooking?: boolean;
  geoLocationDisabled?: boolean;
  locatingUser?: boolean;
  travelClasses: TravelClass[];
  clientMsOffset?: number;
  activeLocationIndex: number;
  regulatoryWarningAirport?: string;
  previousSearchesEnabled: boolean;
  compactWidgetState: CompactWidgetState;
  locationType: LocationType;
}

const initialUxState: UxState = {
  warnings: {},
  loading: {},
  i18n: {
    localizations,
  },
  proceedEnabled: false,
  departureDateEnabled: false,
  returnDateEnabled: false,
  proceedToBooking: false,
  geoLocationDisabled: false,
  locatingUser: false,
  travelClasses: DEFAULT_TRAVEL_CLASSES,
  activeLocationIndex: 0,
  previousSearchesEnabled: false,
  compactWidgetState: CompactWidgetState.CLOSED,
  locationType: null,
};

// == Prices (non-persistent) ==================================================

export interface PriceState {
  histogramData?: HistogramData;
  fullYear?: Prices;
  followingMonth?: Prices;
  startingFrom?: Price;
  availability?: Availability[];
  weeklyFlights?: WeeklyFlights[];
  airCalendar?: AirCalendarPrice[];
  error?: string;
}

const initialPriceState: PriceState = {};

// == Locations (non-persistent) ===============================================

export interface LocationState {
  query?: string;
  results?: Location[];
  allowGeoLocation?: boolean;
  suggestions?: LocationSuggestions;
  trending?: TrendingDestinations;

  /**
   * Defaulting to false, because we don't want to block proceeding to booking flow. During the transition period
   * both endpoints should work, so doing an offer query is safe (even if FPP is enabled for that O&D).
   */
  fppEnabled?: boolean;
}

export const initialLocationState: LocationState = {
  query: '',
  allowGeoLocation: true,
  fppEnabled: false,
};

// == Profile (non-persistent) ===============================================

export interface ProfileState {
  isLoggedIn: boolean;
  memberNumber?: string;
  awardPoints?: number;
  tier?: ProfileTier;
}

export const initialProfileState: ProfileState = {
  isLoggedIn: false,
  memberNumber: '',
  awardPoints: 0,
};

// == BookingState (root) ======================================================
export interface BookingState {
  selection: SelectionState;
  ux: UxState;
  price: PriceState;
  location: LocationState;
  envConfig?: EnvConfig;
  extensionApi?: ExtensionApi;
  amState: AmState;
  currentTab: WidgetTab;
  tabsEnabled: boolean;
  forceUpdate?: boolean;
  enableAurinkomatkat: boolean;
  enableAward: boolean;
  enableAwardIfLoggedIn: boolean;
  enablePromoCode: boolean;
  enableCompactWidget: boolean;
  enableAutoFocus: boolean;
  enableOpenJaw: boolean;
  profile: Profile;
  testVariant: WidgetTestVariant;
  isInitialRender: boolean;
  shouldExpandWidget: boolean;
  contentType: ContentType;
}

export const initialState: BookingState = {
  selection: initialSelectionState,
  ux: initialUxState,
  price: initialPriceState,
  location: initialLocationState,
  amState: initialAmState,
  currentTab: WidgetTab.FLIGHT,
  tabsEnabled: false,
  enableAurinkomatkat: false,
  enableAward: false,
  enableAwardIfLoggedIn: false,
  enablePromoCode: false,
  enableCompactWidget: false,
  enableAutoFocus: true,
  enableOpenJaw: false,
  profile: initialProfileState,
  testVariant: WidgetTestVariant.A,
  isInitialRender: false,
  shouldExpandWidget: false,
  contentType: ContentType.FRONT_PAGE,
};
